import { z } from 'zod';

export const fanGroupMemberSearchFields = [
  'ALL',
  'EMAIL',
  'FIRST_NAME',
  'LAST_NAME',
] as const;

// Keyset cursor for the filtered members search (server-side Country/City/Postal
// code/Tag filters). Mirrors the backend FgoMemberSearchAfterView: lastName is nullable
// (the query sorts last_name NULLS LAST, user_id, so the cursor must be able to sit inside
// the null-last-name tail), userId is always required.
export const FanGroupMemberSearchAfterValidatorSchema = z.object({
  lastName: z.string().nullable(),
  userId: z.string(),
});

export type FanGroupMemberSearchAfter = z.infer<
  typeof FanGroupMemberSearchAfterValidatorSchema
>;

export const FanGroupMembersValidatorSchema = z.object({
  query: z.string(),
  exactCount: z.boolean().optional(),
  searchField: z.enum(fanGroupMemberSearchFields).optional(),
  includeDetails: z.boolean().optional(),
  // Server-side member filters (FGO Users screen phase 2). Empty/absent on both means
  // "no filter" — the request/response stay byte-identical to before these fields existed.
  // AND across locationFilters vs. badgeIds. Within locationFilters, entries are grouped
  // server-side by filter type (COUNTRY/CITY/POSTAL_CODE) -- OR within a type, AND across
  // types, with UNKNOWN (if selected) OR'd against that whole type conjunction (see
  // FgoMemberFilterSql). Within badgeIds, entries OR together.
  // Same "filterType|countryCode|normalizedValue" (or "UNKNOWN") encoding as the waiting-list
  // positions filter, plus this path's own "COUNTRY|<alpha2>" entries (members-only; the
  // waiting-list positions filter does not accept COUNTRY).
  locationFilters: z.array(z.string()).optional(),
  badgeIds: z.array(z.string()).optional(),
  // Explicit member selection (FGO Users screen phase 5 — bulk actions/export): a bounded
  // set of member ids (e.g. a page's worth of checkbox selections) to look up directly,
  // ANDed with any other filter fields above rather than replacing them. Empty/absent means
  // "no id scope", same as the other filter fields.
  userIds: z.array(z.string()).optional(),
  // Query-only callers opt into keyset mode explicitly on the first request; filtered
  // requests also carry the cursor on subsequent pages.
  useKeyset: z.boolean().optional(),
  // Keyset cursor for the filtered path only; ignored by the plain browse/search path.
  after: FanGroupMemberSearchAfterValidatorSchema.optional(),
});

export type FanGroupMembersValidator = z.infer<
  typeof FanGroupMembersValidatorSchema
>;

export type FanGroupMemberSearchField =
  (typeof fanGroupMemberSearchFields)[number];

export const CreateFGOGroupMemberValidatorSchema = z.object({
  numberOfSeats: z.number(),
  nullableOriginalRank: z.number().nullable(),
  nullableRank: z.number().nullable(),
});

export type CreateFGOGroupMemberValidator = z.infer<
  typeof CreateFGOGroupMemberValidatorSchema
>;
